import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { MaterialCountingPieChart } from "../../../components/charts/MaterialCountingPieChart";
import CampaignAnalysisAccordion from "./CampaignAnalysisAccordion";
import CampaignAnalysisChartCard from "./CampaignAnalysisChartCard";
import CampaignDailyHistogram from "./CampaignDailyHistogram";
import CampaignMaterialRemainingChart from "./CampaignMaterialRemainingChart";
import { CHART_COLORS, CHART_HEIGHTS } from "./campaignChartConstants";

const CampaignDistributionSection = ({
  comments,
  campaignPeriod,
  monthlyTrackedObjects,
  errorsByCategoryChartData,
  nonRetraitReasonsChartData,
  emplacementChartData,
  defaultOpen = true,
  storageKey = "distribution",
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const trackedMaterials = campaignPeriod?.trackedMaterials || [];

  const horizontalBarOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: CHART_COLORS.TOOLTIP,
        titleAlign: "center",
        callbacks: {
          label: (context) => `${context.parsed.x}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
      y: {
        ticks: { autoSkip: false },
      },
    },
  };

  return (
    <CampaignAnalysisAccordion
      title={t("campaign.distributionTitle")}
      subtitle={t("campaign.distributionSubtitle")}
      defaultOpen={defaultOpen}
      storageKey={storageKey}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="campaign-analysis-card__empty">{t("Loading")}</div>
        </div>
      ) : (
        <div className="campaign-distribution__layout">
          <CampaignDailyHistogram
            comments={comments}
            campaignPeriod={campaignPeriod}
            variant="distribution"
          />

          <div className="campaign-distribution__grid">
            <CampaignAnalysisChartCard
              title={t("campaign.distributionByLocationTitle")}
            >
              <div
                className="campaign-analysis-card__chart"
                style={{ height: CHART_HEIGHTS.LARGE }}
              >
                {emplacementChartData.total > 0 ? (
                  <MaterialCountingPieChart
                    dataChart={emplacementChartData.values}
                    labelChart={emplacementChartData.labels}
                    materialTotal={emplacementChartData.total}
                    colorChart={emplacementChartData.colors}
                    showMaterialTotal={false}
                    radius={["45%", "70%"]}
                  />
                ) : (
                  <div className="campaign-analysis-card__empty">
                    {t("No data available")}
                  </div>
                )}
              </div>
            </CampaignAnalysisChartCard>

            <CampaignAnalysisChartCard
              title={t("campaign.nonRetraitReasonsTitle")}
            >
              <div
                className="campaign-analysis-card__chart"
                style={{ height: CHART_HEIGHTS.LARGE }}
              >
                {nonRetraitReasonsChartData.total > 0 ? (
                  <Bar
                    data={{
                      labels: nonRetraitReasonsChartData.labels,
                      datasets: [
                        {
                          label: t("Number of alerts"),
                          data: nonRetraitReasonsChartData.values,
                          backgroundColor: CHART_COLORS.PRIMARY,
                          borderColor: CHART_COLORS.PRIMARY,
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={horizontalBarOptions}
                  />
                ) : (
                  <div className="campaign-analysis-card__empty">
                    {t("No data available")}
                  </div>
                )}
              </div>
            </CampaignAnalysisChartCard>

            <CampaignMaterialRemainingChart
              trackedMaterials={trackedMaterials}
              monthlyTrackedObjects={monthlyTrackedObjects}
              comments={comments}
              campaignPeriod={campaignPeriod}
            />

            <CampaignAnalysisChartCard
              title={t("campaign.errorsByCategoryTitle")}
            >
              <div
                className="campaign-analysis-card__chart"
                style={{ height: CHART_HEIGHTS.LARGE }}
              >
                {errorsByCategoryChartData.total > 0 ? (
                  <Bar
                    data={{
                      labels: errorsByCategoryChartData.labels,
                      datasets: [
                        {
                          label: t("Number of objects removed"),
                          data: errorsByCategoryChartData.values,
                          backgroundColor: errorsByCategoryChartData.colors,
                          borderColor: errorsByCategoryChartData.colors,
                          borderWidth: 1,
                        },
                      ],
                    }}
                    options={horizontalBarOptions}
                  />
                ) : (
                  <div className="campaign-analysis-card__empty">
                    {t("No data available")}
                  </div>
                )}
              </div>
            </CampaignAnalysisChartCard>
          </div>
        </div>
      )}
    </CampaignAnalysisAccordion>
  );
};

export default CampaignDistributionSection;
